'use client'

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { defaultBrandSettings } from '../constants/brand-settings'
import { BrandSettingsInterface } from '../types/brand-settings'
import { getGlobalBrand, setGlobalBrand } from '../utils/global-brand'

interface BrandContextValue {
  brand: BrandSettingsInterface
  setBrand: (brand: BrandSettingsInterface) => void
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined)

interface BrandProviderProps {
  initialBrand?: BrandSettingsInterface | null
  children: ReactNode
}

const resolveInitialBrand = (candidate?: BrandSettingsInterface | null): BrandSettingsInterface =>
  candidate ?? defaultBrandSettings

export const BrandProvider = ({ initialBrand, children }: BrandProviderProps): ReactNode => {
  const [brand, setBrand] = useState<BrandSettingsInterface>(() => {
    const resolvedBrand = resolveInitialBrand(initialBrand)

    setGlobalBrand(resolvedBrand)

    return resolvedBrand
  })

  const syncBrand = useCallback((nextBrand: BrandSettingsInterface) => {
    const resolvedBrand = resolveInitialBrand(nextBrand)
    setBrand(resolvedBrand)

    setGlobalBrand(resolvedBrand)
  }, [])

  useEffect(() => {
    const globalBrand = getGlobalBrand()

    if (globalBrand) {
      syncBrand(globalBrand)
    }
  }, [syncBrand])

  useEffect(() => {
    setGlobalBrand(brand)
  }, [brand])

  const contextValue = useMemo<BrandContextValue>(
    () => ({ brand, setBrand: syncBrand }),
    [brand, syncBrand],
  )

  return <BrandContext.Provider value={contextValue}>{children}</BrandContext.Provider>
}

export const useBrand = (): BrandContextValue => {
  const context = useContext(BrandContext)

  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider')
  }

  return context
}
